<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useRevendaStore } from '@/stores/revenda';
import { useNotificationStore } from '@/stores/notification';
import { loadGoogleMapsApi } from '@/plugins/googleMapsLoader';

const route = useRoute();
const router = useRouter();
const revendaStore = useRevendaStore();
const notificationStore = useNotificationStore();

const loading = ref(true);
const mapLoading = ref(true);
const mapError = ref(null);
const poligonosLoading = ref(false);
const editMode = ref(false); // Add a ref to track if we're in polygon creation mode
const isDrawing = ref(false); // Add a ref to track if we're currently drawing a polygon
const deleteConfirmation = ref(false); // Add a ref for delete confirmation dialog

let map = null;
let polygons = [];
let revendaMarker = null; // Add a variable to store the marker for the revenda's location
let drawingPath = [];
let tempPolygon = null;
let tempMarkers = [];
let mapClickListener = null; // Add this variable at component level
const mapContainer = ref(null);

const revenda = computed(() => revendaStore.revenda);
const error = computed(() => revendaStore.error);
const poligonos = computed(() => revendaStore.poligonos || []);

// Colors for the polygons
const polygonColors = [
  '#FF0000', // Red
  '#0000FF', // Blue
  '#00FF00', // Green
  '#FFFF00', // Yellow
  '#FFA500'  // Orange
];

const selectedPolygonIndex = ref(null);
const isSaveEnabled = ref(false);
const saving = ref(false);
const modifiedPolygons = ref([]);

// Keep track of whether changes have been made that need to be saved
const changesMade = ref(false);

// Add new state for map styling
const hideMapLabels = ref(false);

// Define map styles to hide POIs but keep streets and other geographic features
const mapStyles = {
  // Normal map style with everything visible
  withAllFeatures: [],
  
  // Editing mode: Hide POIs but keep streets and important geographic labels
  withoutPOIs: [
    {
      // Hide all points of interest
      featureType: "poi",
      elementType: "labels",
      stylers: [{ visibility: "off" }]
    },
    {
      // Hide business points
      featureType: "poi.business",
      stylers: [{ visibility: "off" }]
    },
    {
      // Keep street labels visible
      featureType: "road",
      elementType: "labels",
      stylers: [{ visibility: "on" }]
    },
    {
      // Keep neighborhood names
      featureType: "administrative.neighborhood",
      elementType: "labels",
      stylers: [{ visibility: "on" }]
    }
  ]
};

// Function to toggle POI visibility
const togglePOIVisibility = (hidePOIs) => {
  if (!map) return;
  
  map.setOptions({
    styles: hidePOIs ? mapStyles.withoutPOIs : mapStyles.withAllFeatures
  });
};

// Function to fetch polygons data
const buscarPoligonos = async () => {
  if (!revenda.value || !revenda.value.id) return;
  
  poligonosLoading.value = true;
  
  try {
    const response = await revendaStore.buscarPoligonosRevenda(revenda.value.id);
    
    if (!response.success) {
      throw new Error(response.message || 'Erro ao carregar polígonos');
    }
    
    // Polygons should now be available in the revendaStore.poligonos
    // We'll update the map when this finishes
    addPolygonsToMap();
    
  } catch (err) {
    console.error('Erro ao buscar polígonos:', err);
    notificationStore.showNotification('Erro ao carregar áreas da revenda', 'error');
  } finally {
    poligonosLoading.value = false;
  }
};

// Function to initialize the map
const initializeMap = async () => {
  mapLoading.value = true;
  mapError.value = null;

  try {
    await loadGoogleMapsApi();
    
    // Default center (São Paulo, Brasil)
    let center = { lat: -23.550520, lng: -46.633309 };
    
    // If we have coordinates from the revenda, use them
    if (revenda.value && revenda.value.lat && revenda.value.lon) {
      center = { 
        lat: parseFloat(revenda.value.lat), 
        lng: parseFloat(revenda.value.lon) 
      };
    }

    // Create the map
    map = new google.maps.Map(mapContainer.value, {
      center,
      zoom: 13,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      mapTypeControl: true,
      fullscreenControl: true,
      streetViewControl: true,
      drawingControl: false, // We'll manage drawing with our own buttons
    });

    // Add the revenda marker to the map
    addRevendaMarker();

    // After the map is loaded, fetch the polygons
    buscarPoligonos();
    
  } catch (err) {
    console.error('Error initializing map:', err);
    mapError.value = 'Erro ao carregar o mapa. Verifique sua conexão ou tente novamente mais tarde.';
  } finally {
    mapLoading.value = false;
  }
};

// Function to add a marker for the revenda's location
const addRevendaMarker = () => {
  if (!map || !revenda.value || !revenda.value.lat || !revenda.value.lon) return;
  
  // Remove existing marker if any
  if (revendaMarker) {
    revendaMarker.setMap(null);
  }
  
  const position = {
    lat: parseFloat(revenda.value.lat),
    lng: parseFloat(revenda.value.lon)
  };
  
  // Create a marker with custom styling
  revendaMarker = new google.maps.Marker({
    position,
    map,
    title: revenda.value.nomefantasia || 'Sede da Revenda',
    animation: google.maps.Animation.DROP,
    icon: {
      url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png', // Blue marker to distinguish from polygon points
      scaledSize: new google.maps.Size(40, 40), // Make it slightly larger for visibility
    },
    zIndex: 999 // Keep it above other elements
  });
  
  // Add an info window to show revenda information when clicked
  const infoContent = `
    <div style="padding: 10px; max-width: 300px;">
      <h3 style="margin-top: 0; margin-bottom: 8px; color: #1976D2;">${revenda.value.nomefantasia || 'Sede da Revenda'}</h3>
      <p style="margin-bottom: 5px;"><strong>Endereço:</strong> ${revenda.value.logradouro || ''}, ${revenda.value.numero || ''}</p>
      <p style="margin-bottom: 5px;"><strong>Bairro:</strong> ${revenda.value.bairro || ''}</p>
      <p style="margin-bottom: 0;"><strong>Cidade:</strong> ${revenda.value.cidade || ''} - ${revenda.value.estado || ''}</p>
    </div>
  `;
  
  const infoWindow = new google.maps.InfoWindow({
    content: infoContent
  });
  
  // Open info window when marker is clicked
  google.maps.event.addListener(revendaMarker, 'click', function() {
    infoWindow.open(map, revendaMarker);
  });
};

// Function to add a new polygon
const adicionarNovoPoligono = () => {
  if (!map) return;
  
  // Disable editing on all existing polygons
  polygons.forEach(p => {
    p.setEditable(false);
    p.setOptions({ strokeWeight: 2 });
  });
  
  // Reset selection
  selectedPolygonIndex.value = null;
  
  // Enable drawing mode
  editMode.value = true;
  isDrawing.value = true;
  
  // Hide POIs to avoid accidental clicks
  hideMapLabels.value = true;
  togglePOIVisibility(true);
  
  // Get a new color for this polygon
  const colorIndex = polygons.length % polygonColors.length;
  const color = polygonColors[colorIndex];
  
  // Initialize empty drawing path
  drawingPath = [];
  tempMarkers = [];
  
  // Create temporary polygon that will be updated as user adds points
  tempPolygon = new google.maps.Polygon({
    paths: [],
    strokeColor: color,
    strokeOpacity: 0.8,
    strokeWeight: 3,
    fillColor: color,
    fillOpacity: 0.35,
  });
  
  tempPolygon.setMap(map);
  
  // Add click listener to the map
  mapClickListener = google.maps.event.addListener(map, 'click', (event) => {
    const point = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    };
    
    // Check if we're closing the polygon by clicking near the first point
    if (drawingPath.length >= 3) {
      const firstPoint = drawingPath[0];
      // Calculate distance between clicked point and first point
      const distance = google.maps.geometry.spherical.computeDistanceBetween(
        new google.maps.LatLng(firstPoint.lat, firstPoint.lng),
        event.latLng
      );
      
      // If click is within 50 meters of the first point (increased tolerance), close the polygon
      if (distance < 50) {
        // Add a visualization indicator that the polygon is being closed
        notificationStore.showNotification('Fechando o polígono...', 'info');
        
        // Short delay to allow visual feedback before closing
        setTimeout(() => {
          finishDrawing();
        }, 200);
        return;
      }
    }
    
    // Add the point to the drawing path
    drawingPath.push(point);
    
    // Add a marker at the clicked point with special handling for first point
    let markerIcon = {
      path: google.maps.SymbolPath.CIRCLE,
      scale: 5,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: '#FFFFFF',
      strokeWeight: 2
    };
    
    // Make the first point distinctive to show it's the closing point
    if (drawingPath.length === 1) {
      markerIcon = {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 8,  // Larger
        fillColor: "#FFD700",  // Gold
        fillOpacity: 1,
        strokeColor: '#FF0000', // Red outline
        strokeWeight: 3
      };
    }
    
    const marker = new google.maps.Marker({
      position: event.latLng,
      map: map,
      icon: markerIcon,
      // Add a special animation to the first marker to make it more noticeable
      animation: drawingPath.length === 1 ? google.maps.Animation.BOUNCE : null
    });
    
    // Stop the bouncing animation after a few seconds
    if (drawingPath.length === 1) {
      setTimeout(() => {
        marker.setAnimation(null);
      }, 3000);
    }
    
    tempMarkers.push(marker);
    
    // Update the temporary polygon
    tempPolygon.setPath(drawingPath);
    
    // Show completion instructions after adding 3 points
    if (drawingPath.length === 3) {
      // Highlight the first marker again to indicate it can be clicked to close
      if (tempMarkers.length > 0 && tempMarkers[0]) {
        try {
          tempMarkers[0].setIcon({
            path: google.maps.SymbolPath.CIRCLE,
            scale: 8,  // Larger
            fillColor: "#FFD700",  // Gold
            fillOpacity: 1,
            strokeColor: '#FF0000', // Red outline
            strokeWeight: 3
          });
          
          // Make it bounce again to draw attention
          tempMarkers[0].setAnimation(google.maps.Animation.BOUNCE);
          setTimeout(() => {
            // Check if marker still exists before trying to access it
            if (tempMarkers.length > 0 && tempMarkers[0]) {
              tempMarkers[0].setAnimation(null);
            }
          }, 3000);
        } catch (e) {
          console.warn("Failed to update marker animation, marker might be removed", e);
        }
      }
      
      notificationStore.showNotification(
        'Continue adicionando pontos ou clique no PONTO AMARELO para fechar e concluir o polígono.', 
        'info'
      );
      
      // Add a click listener specifically to the first marker
      if (tempMarkers[0]) {
        google.maps.event.addListener(tempMarkers[0], 'click', () => {
          if (drawingPath.length >= 3) {
            finishDrawing();
          }
        });
      }
    }
  });
  
  // Show instructions to the user
  notificationStore.showNotification(
    'Clique no mapa para adicionar pontos. Adicione pelo menos 3 pontos e feche o polígono clicando próximo ao ponto inicial.', 
    'info'
  );
  
  // Add context menu (right-click) option to complete polygon
  const rightClickListener = google.maps.event.addListener(map, 'rightclick', () => {
    // Only allow completion if we have at least 3 points
    if (drawingPath.length >= 3) {
      finishDrawing();
    } else {
      notificationStore.showNotification(
        'É necessário adicionar pelo menos 3 pontos para formar um polígono.', 
        'warning'
      );
    }
  });
  
  // Function to finish drawing and create the final polygon
  function finishDrawing() {
    // Remove any right-click listener
    if (rightClickListener) {
      google.maps.event.removeListener(rightClickListener);
    }
    
    // Clear any active animations before removing markers
    if (tempMarkers && tempMarkers.length > 0 && tempMarkers[0]) {
      try {
        tempMarkers[0].setAnimation(null);
      } catch (e) {
        console.warn("Failed to clear marker animation, marker might be already removed");
      }
    }
    
    // Remove temporary markers
    if (tempMarkers && tempMarkers.length > 0) {
      tempMarkers.forEach(marker => {
        if (marker) {
          marker.setMap(null);
        }
      });
      tempMarkers = [];
    }
    
    // Remove temporary polygon
    if (tempPolygon) {
      tempPolygon.setMap(null);
      tempPolygon = null;
    }
    
    // Remove map click listener
    if (mapClickListener) {
      google.maps.event.removeListener(mapClickListener);
      mapClickListener = null;
    }
    
    // Close the polygon by adding the first point again if it's not already closed
    if (drawingPath.length >= 3 && 
        (drawingPath[0].lat !== drawingPath[drawingPath.length-1].lat || 
         drawingPath[0].lng !== drawingPath[drawingPath.length-1].lng)) {
      drawingPath.push(drawingPath[0]);
    }
    
    // Create final polygon
    const finalPolygon = new google.maps.Polygon({
      paths: drawingPath,
      strokeColor: color,
      strokeOpacity: 0.8,
      strokeWeight: 3,
      fillColor: color,
      fillOpacity: 0.35,
      editable: true,
      index: polygons.length
    });
    
    finalPolygon.setMap(map);
    
    // Apply enhanced styling for editing
    applyPolygonEditStyling(finalPolygon);
    
    // Add click handler for editing
    google.maps.event.addListener(finalPolygon, 'click', () => {
      enablePolygonEditing(finalPolygon, finalPolygon.index);
    });
    
    // Add path change listeners
    google.maps.event.addListener(finalPolygon.getPath(), 'set_at', () => {
      updatePolygonCoordinates(finalPolygon.index, finalPolygon);
    });
    
    google.maps.event.addListener(finalPolygon.getPath(), 'insert_at', () => {
      updatePolygonCoordinates(finalPolygon.index, finalPolygon);
    });
    
    google.maps.event.addListener(finalPolygon.getPath(), 'remove_at', () => {
      updatePolygonCoordinates(finalPolygon.index, finalPolygon);
    });
    
    // Store the new polygon
    polygons.push(finalPolygon);
    
    // Add the polygon coordinates to our state
    modifiedPolygons.value.push([...drawingPath]);
    
    // Enable save button
    enableSaveButton();
    
    // Set this as the selected polygon
    selectedPolygonIndex.value = polygons.length - 1;
    
    // Exit drawing mode but stay in edit mode
    isDrawing.value = false;
    editMode.value = true;
    
    // Mark that changes were made
    changesMade.value = true;
    
    // Show success message and guidance
    notificationStore.showNotification(
      'Polígono criado. Agora você pode editar sua forma arrastando os pontos e adicionando novos.',
      'success'
    );
  }
};

// Function to delete the selected polygon
const excluirPoligonoSelecionado = () => {
  if (selectedPolygonIndex.value === null) {
    notificationStore.showNotification('Nenhum polígono selecionado para excluir', 'warning');
    return;
  }
  
  deleteConfirmation.value = true;
};

// Function to confirm polygon deletion
const confirmarExclusaoPoligono = () => {
  if (selectedPolygonIndex.value === null) return;
  
  const indexToRemove = selectedPolygonIndex.value;
  
  // Remove from map
  if (polygons[indexToRemove]) {
    google.maps.event.clearInstanceListeners(polygons[indexToRemove]);
    polygons[indexToRemove].setMap(null);
  }
  
  // Remove from arrays
  modifiedPolygons.value.splice(indexToRemove, 1);
  polygons.splice(indexToRemove, 1);
  
  // Update indices for remaining polygons
  polygons.forEach((polygon, idx) => {
    polygon.index = idx;
  });
  
  // Reset selection
  selectedPolygonIndex.value = null;
  deleteConfirmation.value = false;
  
  // Mark that changes were made
  changesMade.value = true;
  
  // Enable save button
  enableSaveButton();
  
  notificationStore.showNotification('Polígono removido. Clique em "Salvar Alterações" para persistir.', 'info');
};

// Cancel deletion
const cancelarExclusaoPoligono = () => {
  deleteConfirmation.value = false;
};

// Function to add polygon edit styling with more visible vertex controls
const applyPolygonEditStyling = (polygon) => {
  // If Google Maps API is not loaded yet, exit
  if (!window.google || !window.google.maps) return;
  
  // Create custom vertex style for the polygon
  const polyOptions = {
    editable: true,
    strokeWeight: 3,
    fillOpacity: 0.35,
    zIndex: 100 // Ensure it's above other polygons
  };
  
  polygon.setOptions(polyOptions);
  
  // Use a more controlled approach to style the map controls
  const applyMapControlStyles = () => {
    // Create a style element specifically for the map controls
    const styleId = 'google-maps-custom-controls-style';
    let styleEl = document.getElementById(styleId);
    
    // Remove any existing style element to prevent accumulation
    if (styleEl) {
      styleEl.remove();
    }
    
    // Create a new style element with scoped selectors
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    
    // Use highly specific selectors with attribute checks to avoid affecting other elements
    styleEl.innerHTML = `
      /* Target only Google Maps vertex markers */
      #map-container div[role="button"][style*="background-image"] {
        background-color: white !important;
        border: 6px solid #1976D2 !important;
        border-radius: 50% !important;
        box-shadow: 0 0 12px rgba(0, 0, 0, 0.8) !important;
        cursor: move !important;
        width: 30px !important;
        height: 30px !important;
        z-index: 1000 !important;
        opacity: 1 !important;
      }
      
      #map-container div[role="button"][style*="background-image"]:hover {
        background-color: #2196F3 !important;
        border-color: white !important;
        transform: translate(-50%, -50%) scale(1.3) !important;
        box-shadow: 0 0 18px rgba(25, 118, 210, 0.9) !important;
      }
      
      #map-container div[style*="background-image"][style*="transform"]:not([role="button"]) {
        background-color: rgba(255, 255, 255, 0.9) !important;
        border: 5px solid #4CAF50 !important;
        border-radius: 50% !important;
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.7) !important;
        width: 22px !important;
        height: 22px !important;
        cursor: pointer !important;
        opacity: 0.95 !important;
      }
      
      #map-container div[style*="background-image"][style*="transform"]:not([role="button"]):hover {
        background-color: #4CAF50 !important;
        border-color: white !important;
      }
    `;
    
    // Add the style to document head for consistent application
    document.head.appendChild(styleEl);
  };
  
  // Apply styles after a delay to ensure all Google Maps elements are rendered
  setTimeout(applyMapControlStyles, 100);
  
  // Set up an observer to handle dynamically added elements
  const setupMutationObserver = () => {
    // Disconnect any existing observer
    if (window.mapControlsObserver) {
      window.mapControlsObserver.disconnect();
    }
    
    const mapElement = document.getElementById('map-container');
    if (!mapElement) return;
    
    // Create a new observer to detect when Google adds new control points
    window.mapControlsObserver = new MutationObserver(() => {
      // Re-apply styles when DOM changes
      applyMapControlStyles();
    });
    
    // Start observing
    window.mapControlsObserver.observe(mapElement, {
      childList: true,
      subtree: true
    });
  };
  
  // Set up the observer after a short delay
  setTimeout(setupMutationObserver, 200);
};

// Track original polygon coordinates before editing
const originalPolygonCoords = ref({});

// Modified enablePolygonEditing function to save original state
const enablePolygonEditing = (polygon, index) => {
  // Disable editing on all polygons
  polygons.forEach(p => {
    p.setEditable(false);
    p.setOptions({ strokeWeight: 2 });
  });
  
  // Before enabling edit mode, save the original coordinates
  if (polygon && polygon.getPath()) {
    const path = polygon.getPath();
    const originalCoords = [];
    
    for (let i = 0; i < path.getLength(); i++) {
      const point = path.getAt(i);
      originalCoords.push({
        lat: point.lat(),
        lng: point.lng()
      });
    }
    
    // Save original state for this polygon
    originalPolygonCoords.value[index] = [...originalCoords];
  }
  
  // Enable editing on the clicked polygon with enhanced styling
  applyPolygonEditStyling(polygon);
  selectedPolygonIndex.value = index;
  
  // Enable the save button
  enableSaveButton();
  
  // Hide POIs when editing
  hideMapLabels.value = true;
  togglePOIVisibility(true);
  
  // Show editing instructions
  notificationStore.showNotification(
    'Clique e arraste os pontos brancos para modificar a forma do polígono. Pontos intermediários podem ser usados para adicionar novos vértices.',
    'info'
  );
};

// Enhanced cancel editing function to handle different scenarios
const cancelarEdicao = () => {
  // If we're currently drawing, clean up the drawing without affecting other changes
  if (isDrawing.value) {
    // Remove map click listener
    if (mapClickListener) {
      google.maps.event.removeListener(mapClickListener);
      mapClickListener = null;
    }
    
    // Stop any animations before cleanup
    if (tempMarkers && tempMarkers.length > 0) {
      for (const marker of tempMarkers) {
        if (marker) {
          try {
            marker.setAnimation(null);
          } catch (e) {
            // Ignore errors for markers that might be already removed
          }
          marker.setMap(null);
        }
      }
      tempMarkers = [];
    }
    
    // Remove temporary polygon
    if (tempPolygon) {
      tempPolygon.setMap(null);
      tempPolygon = null;
    }
    
    isDrawing.value = false;
    drawingPath = [];
    editMode.value = false;
    
    // Restore POI visibility when exiting drawing mode
    hideMapLabels.value = false;
    togglePOIVisibility(false);
    
    notificationStore.showNotification('Criação de polígono cancelada', 'info');
    return;
  }
  
  // If we have a selected polygon, restore its original state
  if (selectedPolygonIndex.value !== null) {
    const index = selectedPolygonIndex.value;
    const polygon = polygons[index];
    
    // If we have the original coordinates, restore them
    if (originalPolygonCoords.value[index]) {
      if (polygon) {
        // Restore original shape
        const originalCoords = originalPolygonCoords.value[index];
        const path = polygon.getPath();
        
        // Clear current path
        while (path.getLength() > 0) {
          path.removeAt(0);
        }
        
        // Add original points back
        for (const point of originalCoords) {
          path.push(new google.maps.LatLng(point.lat, point.lng));
        }
        
        // Also restore in our tracked state
        modifiedPolygons.value[index] = [...originalCoords];
        
        // Disable editing
        polygon.setEditable(false);
        polygon.setOptions({ strokeWeight: 2 });
        
        notificationStore.showNotification('Edições canceladas', 'info');
      }
    }
    
    // Clean up original coordinates reference
    delete originalPolygonCoords.value[index];
    
    // Reset selection
    selectedPolygonIndex.value = null;
    
    // If we're not selecting any polygon anymore, restore POI visibility
    if (!editMode.value) {
      hideMapLabels.value = false;
      togglePOIVisibility(false);
    }
    
    return;
  }
  
  // Reset edit mode if no specific polygon is selected
  editMode.value = false;
  isSaveEnabled.value = false;
  
  // Make sure POIs are visible when exiting all edit modes
  hideMapLabels.value = false;
  togglePOIVisibility(false);
};

// Function to add polygons to the map
const addPolygonsToMap = () => {
  if (!map) return;

  // Clear existing polygons
  polygons.forEach(polygon => {
    google.maps.event.clearInstanceListeners(polygon);
    polygon.setMap(null);
  });
  polygons = [];
  
  // Make sure the revenda marker is showing (in case it was removed)
  addRevendaMarker();

  // Get polygons data from store
  const polygonData = poligonos.value;
  
  if (!polygonData || polygonData.length === 0) {
    console.log('No polygon data available');
    return;
  }

  // Store a copy of the original data for comparison
  modifiedPolygons.value = JSON.parse(JSON.stringify(polygonData));

  // Add each polygon to the map
  polygonData.forEach((coords, index) => {
    const color = polygonColors[index % polygonColors.length];

    // Make sure all coordinates are properly formatted with numeric lat/lng
    const validCoords = coords.map(coord => ({
      lat: parseFloat(coord.lat),
      lng: parseFloat(coord.lng)
    }));

    const polygon = new google.maps.Polygon({
      paths: validCoords,
      strokeColor: color,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: color,
      fillOpacity: 0.35,
      editable: false, // Initially not editable
      index: index // Store the index for reference
    });

    polygon.setMap(map);
    
    // Add click handler to enable editing with enhanced styling
    google.maps.event.addListener(polygon, 'click', () => {
      enablePolygonEditing(polygon, index);
    });
    
    // Add path change listeners to track modifications
    google.maps.event.addListener(polygon.getPath(), 'set_at', () => {
      updatePolygonCoordinates(index, polygon);
    });
    
    google.maps.event.addListener(polygon.getPath(), 'insert_at', () => {
      updatePolygonCoordinates(index, polygon);
    });
    
    google.maps.event.addListener(polygon.getPath(), 'remove_at', () => {
      updatePolygonCoordinates(index, polygon);
    });
    
    polygons.push(polygon);
  });

  // After adding all polygons
  if (polygons.length > 0) {
    // Fit map bounds to include all polygons and the revenda location
    const bounds = new google.maps.LatLngBounds();
    
    // Add revenda location to bounds
    if (revenda.value && revenda.value.lat && revenda.value.lon) {
      bounds.extend({
        lat: parseFloat(revenda.value.lat),
        lng: parseFloat(revenda.value.lon)
      });
    }
    
    polygonData.forEach(coords => {
      coords.forEach(coord => {
        bounds.extend({ lat: parseFloat(coord.lat), lng: parseFloat(coord.lng) });
      });
    });
    
    map.fitBounds(bounds);
  }
  
  // Reset edit mode
  editMode.value = false;
  
  // Ensure POIs are visible when loading is complete
  hideMapLabels.value = false;
  togglePOIVisibility(false);
};

// Function to update the coordinates for a modified polygon
const updatePolygonCoordinates = (index, polygon) => {
  const path = polygon.getPath();
  const updatedCoords = [];
  
  // Extract coordinates from path
  for (let i = 0; i < path.getLength(); i++) {
    const point = path.getAt(i);
    updatedCoords.push({
      lat: point.lat(),
      lng: point.lng()
    });
  }
  
  // Update the modified polygons array
  modifiedPolygons.value[index] = updatedCoords;
  
  // Mark that changes have been made
  changesMade.value = true;
};

// Function to enable the save button
const enableSaveButton = () => {
  isSaveEnabled.value = true;
};

// Function to save the modified polygons
const saveChanges = async () => {
  if (!revenda.value || !revenda.value.id) {
    notificationStore.showNotification('ID da revenda inválido', 'error');
    return;
  }
  
  saving.value = true;
  
  try {
    // Get all the current polygon data
    const allPolygons = [];
    
    // For each polygon, extract its current path
    polygons.forEach((polygon, idx) => {
      const path = polygon.getPath();
      const coordinates = [];
      
      for (let i = 0; i < path.getLength(); i++) {
        const point = path.getAt(i);
        coordinates.push({
          lat: point.lat(),
          lng: point.lng()
        });
      }
      
      allPolygons.push(coordinates);
    });
    
    // Save all polygons to the API
    const response = await revendaStore.salvarPoligonosRevenda(revenda.value.id, allPolygons);
    
    if (response.success) {
      notificationStore.showNotification(response.message || 'Áreas da revenda atualizadas com sucesso', 'success');
      
      // Disable editing and reset selection
      polygons.forEach(p => {
        p.setEditable(false);
        p.setOptions({ strokeWeight: 2 });
      });
      
      selectedPolygonIndex.value = null;
      isSaveEnabled.value = false;
      editMode.value = false;
      changesMade.value = false; // Reset changes tracker after successful save
      
      // Restore POI visibility after saving
      hideMapLabels.value = false;
      togglePOIVisibility(false);
    } else {
      throw new Error(response.message || 'Erro ao salvar áreas da revenda');
    }
  } catch (err) {
    console.error('Erro ao salvar áreas da revenda:', err);
    notificationStore.showNotification(err.message || 'Erro ao salvar áreas da revenda', 'error');
  } finally {
    saving.value = false;
  }
};

// Add a function to handle exiting edit mode
const exitEditMode = () => {
  // Restore POI visibility
  hideMapLabels.value = false;
  togglePOIVisibility(false);
  
  // Reset editing state
  selectedPolygonIndex.value = null;
  editMode.value = false;
  isDrawing.value = false;
};

// Modify the watch for editing state to better control POI visibility
watch([editMode, isDrawing, selectedPolygonIndex], ([newEditMode, newIsDrawing, newSelectedIndex], [oldEditMode, oldIsDrawing, oldSelectedIndex]) => {
  const shouldHidePOIs = newEditMode || newIsDrawing || newSelectedIndex !== null;
  const wasHidingPOIs = oldEditMode || oldIsDrawing || oldSelectedIndex !== null;
  
  // Only update if there's a change in the hiding state
  if (hideMapLabels.value !== shouldHidePOIs) {
    hideMapLabels.value = shouldHidePOIs;
    togglePOIVisibility(shouldHidePOIs);
    
    // If we're exiting edit/draw mode, show a helpful notification
    if (wasHidingPOIs && !shouldHidePOIs) {
      notificationStore.showNotification('Modo de edição encerrado. Voltando à visualização normal do mapa.', 'info');
    }
  }
});

// Also handle the case when a user selects "Volta para Revenda" button
const voltarParaDetalhes = () => {
  // If there are unsaved changes, confirm before leaving
  if (changesMade.value) {
    const confirmLeave = confirm('Existem alterações não salvas. Deseja sair mesmo assim?');
    if (!confirmLeave) {
      return;
    }
  }
  
  // Restore map to normal view before navigating away
  hideMapLabels.value = false;
  togglePOIVisibility(false);
  
  // Navigate to the details page
  router.push({ name: 'revenda-detalhes', params: { id: route.params.id } });
};

// Fetch the revenda data when the component mounts
onMounted(async () => {
  const revendaId = route.params.id;
  if (!revendaId) {
    notificationStore.showNotification('ID da revenda não fornecido', 'error');
    router.push({ name: 'revendas' });
    return;
  }
  
  loading.value = true;
  
  try {
    // If we don't already have the revenda data, fetch it
    if (!revenda.value || revenda.value.id !== revendaId) {
      await revendaStore.buscarRevendaPorId(revendaId);
    }

    if (error.value) {
      notificationStore.showNotification(`Erro ao carregar dados da revenda: ${error.value}`, 'error');
    }
  } catch (err) {
    console.error('Erro ao carregar dados da revenda:', err);
    notificationStore.showNotification('Erro ao carregar dados da revenda', 'error');
  } finally {
    loading.value = false;
    // Initialize the map after we've loaded the revenda data
    initializeMap();
  }
});

// Cleanup when component is unmounted
onUnmounted(() => {
  // Clean up any active map click listeners
  if (mapClickListener) {
    google.maps.event.removeListener(mapClickListener);
    mapClickListener = null;
  }
  
  // Clean up any temporary markers
  if (tempMarkers && tempMarkers.length) {
    tempMarkers.forEach(marker => marker.setMap(null));
    tempMarkers = [];
  }
  
  // Clean up temporary polygon
  if (tempPolygon) {
    tempPolygon.setMap(null);
    tempPolygon = null;
  }
  
  // Clean up the revenda marker
  if (revendaMarker) {
    revendaMarker.setMap(null);
    revendaMarker = null;
  }
  
  if (polygons.length > 0) {
    polygons.forEach(polygon => {
      // Remove event listeners to prevent memory leaks
      google.maps.event.clearInstanceListeners(polygon);
      polygon.setMap(null);
    });
    polygons = [];
  }
  
  // Remove the custom style element
  const styleEl = document.getElementById('google-maps-custom-controls-style');
  if (styleEl) {
    styleEl.remove();
  }
  
  // Disconnect the mutation observer
  if (window.mapControlsObserver) {
    window.mapControlsObserver.disconnect();
    window.mapControlsObserver = null;
  }
  
  // Reset map styles to normal
  if (map) {
    map.setOptions({ styles: [] });
  }
});
</script>

<template>
  <div class="map-page">
    <!-- Loading state -->
    <div v-if="loading && !revenda" class="d-flex justify-center align-center" style="min-height: 300px;">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </div>
    
    <!-- Error state -->
    <v-alert v-else-if="error" type="error" class="mb-4">
      {{ error }}
      <div class="mt-2">
        <v-btn color="primary" @click="voltarParaDetalhes">Voltar para Detalhes</v-btn>
      </div>
    </v-alert>
    
    <!-- Map container -->
    <v-card v-else class="map-card mb-4">
      <v-card-text class="map-card-content pa-0">
        <div v-if="mapError" class="text-center py-4">
          <v-icon color="error" size="large" class="mb-2">mdi-map-marker-alert</v-icon>
          <h3 class="text-h6 mb-2 text-error">{{ mapError }}</h3>
          <v-btn color="primary" @click="initializeMap">Tentar novamente</v-btn>
        </div>
        
        <div v-else class="map-container">
          <div ref="mapContainer" id="map-container" class="map-element"></div>
          
          <div v-if="mapLoading || poligonosLoading" class="map-loading-overlay d-flex justify-center align-center">
            <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          </div>
          
          <!-- Map Controls Overlay -->
          <div class="map-controls pa-2">
            <v-card variant="outlined" class="pa-2">
              <div class="d-flex flex-column">
                <v-btn 
                  color="success" 
                  variant="elevated" 
                  prepend-icon="mdi-shape-polygon-plus"
                  class="mb-2"
                  @click="adicionarNovoPoligono"
                  :disabled="editMode || isDrawing"
                >
                  Adicionar
                </v-btn>
                
                <v-btn 
                  color="error" 
                  variant="elevated" 
                  prepend-icon="mdi-delete"
                  class="mb-2"
                  @click="excluirPoligonoSelecionado"
                  :disabled="selectedPolygonIndex === null"
                >
                  Excluir
                </v-btn>

                <v-btn 
                  color="grey" 
                  variant="elevated" 
                  prepend-icon="mdi-cancel"
                  @click="cancelarEdicao"
                  :disabled="!editMode && !isSaveEnabled"
                >
                  Cancelar
                </v-btn>
              </div>
            </v-card>
          </div>
          
          
          
          <div class="map-info pa-4">
            <v-card variant="outlined" class="pa-2">
              <h3 class="text-h6 mb-2">Informações da Área</h3>
              <p class="mb-1">
                <strong>Revenda:</strong> {{ revenda?.nomefantasia || 'N/A' }}
              </p>
              <p class="mb-1">
                <strong>Endereço:</strong> {{ revenda?.logradouro || 'N/A' }}, {{ revenda?.numero || 'N/A' }}, {{ revenda?.bairro }}
              </p>
              <p class="mb-1">
                <strong>Cidade:</strong> {{ revenda?.cidade || 'N/A' }} - {{ revenda?.estado || 'N/A' }}
              </p>
              <p class="mb-0">
                <strong>Polígonos:</strong> {{ poligonos.length }} áreas definidas
              </p>
              <div v-if="selectedPolygonIndex !== null" class="mt-2 pt-2 border-t">
                <p class="mb-1">
                  <strong>Polígono selecionado:</strong> {{ selectedPolygonIndex + 1 }}
                </p>
                <p class="text-caption">
                  Clique e arraste os pontos para editar a forma
                </p>
              </div>
              <div v-else-if="editMode" class="mt-2 pt-2 border-t">
                <p class="text-caption">
                  <v-chip color="warning" size="small">Novo polígono</v-chip>
                  Clique e arraste os pontos para ajustar a forma
                </p>
              </div>
            </v-card>
          </div>
        </div>
      </v-card-text>
    </v-card>
    
    <!-- Action buttons moved outside the map - Only keep the "Back" button here -->
     <v-row v-if="revenda && !error && !loading">
      <v-col cols="6" sm="6">
        <v-btn
          color="primary"
          block
          prepend-icon="mdi-content-save"
          :disabled="!isSaveEnabled"
          :loading="saving"
          @click="saveChanges"
        >
          Salvar Alterações
        </v-btn>
      </v-col>
      <v-col cols="6" sm="6">
        <v-btn
          variant="outlined"
          color="grey"
          block
          prepend-icon="mdi-arrow-left"
          @click="voltarParaDetalhes"
        >
          Voltar para Revenda
        </v-btn>
      </v-col>
    </v-row>


    <!-- Delete confirmation dialog -->
    <v-dialog v-model="deleteConfirmation" max-width="400">
      <v-card>
        <v-card-title class="text-h5">
          Confirmar exclusão
        </v-card-title>
        <v-card-text>
          Deseja realmente excluir esta área? Esta ação não pode ser desfeita sem salvar as alterações.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="cancelarExclusaoPoligono">
            Cancelar
          </v-btn>
          <v-btn color="error" @click="confirmarExclusaoPoligono" variant="elevated">
            Excluir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    

  </div>
</template>

<style scoped>
.map-page {
  display: flex;
  flex-direction: column;
  height: auto;
}

.map-card {
  margin-bottom: 16px;
  height: calc(100vh - 150px);
  min-height: 500px; 
}

.map-card-content {
  height: 100%;
  padding: 0;
}

.map-container {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  overflow: hidden;
}

.map-element {
  width: 100%;
  height: 100%;
}

.map-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.7);
  z-index: 10;
}

.map-info {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 300px;
  z-index: 5;
}

/* New control panel style */
.map-controls {
  position: absolute;
  top: 10px;
  left: 10px;
  width: 180px;
  z-index: 5;
}

/* New style for the save button */
.save-button {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;
}

.border-t {
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

</style>